<template>
  <div class="stripe-box anim-fade-in">
    <div
      v-if="formCreationError"
      style="background: var(--clr-error); border-radius: 8px"
      class="pa-4 mb-4"
    >
      <div class="grow text-small weight_500">
        {{ $t('payment.error.status_stripe_FORM_CREATION_ERROR') }}} <OpenChatLink />.
        <div>
          <v-btn
            variant="outlined"
            size="small"
            class="mt-2"
            @click="copyToClipboard"
            >CLICK TO COPY ERROR TEXT</v-btn
          >
          <div
            v-if="errorCopied"
            class="pt-2"
          >
            Error text copied
          </div>
        </div>
        <div
          class="mt-2 text-smallest"
          style="max-height: 3rem; overflow: auto"
        >
          ---
          {{ formCreationError }}
          ---
        </div>
      </div>
    </div>

    <GwMessage
      v-if="stripeError || customerCreatedError"
      :message="{
        type: 'error',
        text: $t('payment.error.status_stripe_failed_to_create_customer'),
        hide: true,
      }"
      class="mb-4"
    >
      <template #default>
        <div v-if="customerCreatedError">
          {{ $t('payment.error.status_stripe_failed_to_create_customer') }}
        </div>
        <div v-else-if="stripeError ? stripeError.message : false">
          {{ stripeError.message }}
        </div>
        <div v-else>
          {{
            stripeError
              ? stripeError.status === 'CARD_DECLINED'
                ? $t('payment.error.status_stripe_CARD_DECLINED')
                : $t('payment.error.status_stripe_UNKNOWN_ERROR')
              : $t('payment.error.status_stripe_UNKNOWN_ERROR')
          }}
          <OpenChatLink v-if="stripeError ? stripeError.status !== 'CARD_DECLINED' : true" />.
        </div>
      </template>
    </GwMessage>

    <form
      v-show="!customerCreatedError"
      id="payment-form"
      class="anim-fade-in"
    >
      <div class="position-relative">
        <div class="input-cards">
          <unionpay class="mr-1" />
          <visa class="mr-1" />
          <amex class="mr-1" />
          <mastercard class="mr-1" />
          <div class="csst f-center icon-lock">
            <lock class="mr-1 no-events" />
            <div
              class="csst_content text-nowrap"
              style="top: -2rem; right: 0"
            >
              {{ $t('tooltips.lock_ecrypted') }}
            </div>
          </div>
        </div>
        {{ $t('payment.credit_card_number') }}
        <div
          id="card-number"
          :class="{ 'error-input': errors.cardNumber }"
        ></div>
        <div
          v-if="errors.cardNumber"
          class="stripe-error"
        >
          {{ errors.cardNumber }}
        </div>
      </div>

      <div class="halved mt-4">
        <div>
          {{ $t('payment.credit_card_exiration') }}
          <div
            id="card-expiry"
            :class="{ 'error-input': errors.cardExpiry }"
          ></div>
          <div class="stripe-error">{{ errors.cardExpiry }}</div>
        </div>
        <div>
          {{ $t('payment.credit_card_cvc') }}
          <div
            id="card-cvc"
            :class="{ 'error-input': errors.cardCvc }"
          ></div>
          <div class="stripe-error">{{ errors.cardCvc }}</div>
        </div>
      </div>

      <div
        v-if="!canCheckout"
        class="mt-8 plan-box_btn cursor-not-allowed"
      >
        <v-btn
          class="w-100 no-events"
          block
          size="large"
          color="#9e9e9e"
        >
          {{ $t('payment.check_out') }}
        </v-btn>
      </div>

      <div
        v-else-if="customerCreatedLoading"
        class="mt-8 plan-box_btn cursor-loading"
      >
        <v-btn
          class="w-100 no-events"
          block
          size="large"
          color="#9e9e9e"
          :loading="true"
        >
        </v-btn>
      </div>
      <div
        v-else
        class="mt-8 plan-box_btn"
      >
        <v-btn
          color="primary"
          data-tst="btn_check_out"
          block
          size="large"
          class="w-100 anim-slide-shine"
          :loading="localLoading"
          :class="{ 'no-events': localLoading }"
          @click="createPaymentMethod"
        >
          {{ $t('payment.check_out') }}
        </v-btn>
      </div>

      <slot
        name="text"
        :tierPlan="selectedTierPlan"
      />

      <div
        v-if="safeBadges"
        class="safe-secure-checkout mt-8"
      >
        <a
          target="_blank"
          href="https://letsencrypt.org/"
        >
          <v-icon
            color="#90caf9"
            class="mr-2"
            >mdi-lock</v-icon
          >
          {{ $t('payment.safe_secure_checkout') }}
        </a>
      </div>

      <div
        v-if="safeBadges"
        class="svg-badges mt-4 mx-auto"
      >
        <div class="svg-badges_svgs">
          <a
            target="_blank"
            href="https://stripe.com/"
            class="svg-stripe"
          >
            <StripeSvg />
          </a>
          <a
            target="_blank"
            href="https://letsencrypt.org/"
            class="svg-encrypt"
          >
            <EncryptSvg />
          </a>
        </div>
        <div class="refund-guarantee-message">
          <i class="mdi mdi-shield-check refund-guarantee-message_icon"></i>
          {{
            $t('subscription.refund_quarantee_short', {
              refund_quarantee_num: 7,
            })
          }}
        </div>
      </div>
    </form>

    <div v-if="!productionENV">
      <div class="mt-8">4242424242424242 Succeeds</div>
      <div>4000002500003155 3dsecure</div>
      <div>4000008260003178 insufficient_funds.</div>
      <div>4000000000000341 Succeeds but fails payment of a subscription</div>
      <div>01/25</div>
      <div>555</div>
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import GwMessage from '@/components/Messages/GwMessage'
import StripeSvg from '@/assets/payment/stripe.svg'
import EncryptSvg from '@/assets/payment/encrypt.svg'
import amex from '@/assets/payment/amex.svg'
import visa from '@/assets/payment/visa.svg'
import mastercard from '@/assets/payment/mastercard.svg'
import unionpay from '@/assets/payment/unionpay.svg'
import lock from '@/assets/payment/lock.svg'
// https://stripe.com/docs/billing/subscriptions/fixed-price
// https://stripe.com/docs/testing#cards
import { gtm_sub_purchase_02 } from '@/helpers/gtm'
import { seg_track, seg_getProduct, seg_getTotalAndCurrency } from '@/helpers/segment'
import payment_mix from '@/mixins/payment_mix'
import links_mix from '@/mixins/links_mix'
import OpenChatLink from '@/components/Payments/OpenChatLink.vue'
import { logSentry } from '@gto/sentry'
import { loadScript } from 'vue-plugin-load-script'

export default {
  name: 'StripeGateway',
  components: {
    OpenChatLink,
    GwMessage,
    StripeSvg,
    EncryptSvg,
    amex,
    visa,
    mastercard,
    unionpay,
    lock,
  },
  mixins: [payment_mix, links_mix],
  props: {
    onSuccessPayment: {
      type: Function,
      default: () => {
        throw new Error('onSuccessPayment is required')
      },
    },
    // checkoutBtn: { type: Boolean, default: true },
    safeBadges: { type: Boolean, default: true },
  },
  data() {
    return {
      customGame: 'CASH',
      stripe: null,
      cardNumber: null,

      errorCopied: false,
      formCreationError: false,

      errors: {
        cardNumber: '',
        cardExpiry: '',
        cardCvc: '',
      },
      completed: {
        cardNumber: false,
        cardExpiry: false,
        cardCvc: false,
      },

      localLoading: false,
      stripeError: null,
    }
  },
  created() {
    this.initialize()
  },
  methods: {
    copyToClipboard(actionRange) {
      const err = this.formCreationError
      const str = JSON.stringify(err, Object.getOwnPropertyNames(err))
      navigator.clipboard.writeText(str)
      this.errorCopied = true
    },
    reset() {
      window.location.reload()
    },
    initialize() {
      this.$store.dispatch('payments_subscription_stripe_customer')
      loadScript('https://js.stripe.com/v3/')
        .then(s => {
          const { Stripe } = window
          if (Stripe) {
            try {
              this.stripe = Stripe(import.meta.env.VITE_STRIPE_KEY)
              const elements = this.stripe.elements()
              const options = {
                style: { invalid: { color: '#ef5350' } },
              }
              this.cardNumber = elements.create('cardNumber', options)
              this.cardNumber.mount('#card-number')
              this.cardNumber.on('change', ({ error, ...rest }) => {
                // console.log(rest, error)
                if (error) {
                  this.errors.cardNumber = error.message
                } else {
                  this.errors.cardNumber = ''
                }
                this.completed.cardNumber = rest.complete
              })
              const cardExpiry = elements.create('cardExpiry', options)
              cardExpiry.mount('#card-expiry')
              cardExpiry.on('change', ({ error, ...rest }) => {
                if (error) {
                  this.errors.cardExpiry = error.message
                } else {
                  this.errors.cardExpiry = ''
                }
                this.completed.cardExpiry = rest.complete
              })
              const cardCvc = elements.create('cardCvc', options)
              cardCvc.mount('#card-cvc')
              cardCvc.on('change', ({ error, ...rest }) => {
                // console.log(rest, error)
                if (error) {
                  this.errors.cardCvc = error.message
                } else {
                  this.errors.cardCvc = ''
                }
                this.completed.cardCvc = rest.complete
              })

              const form = document.getElementById('payment-form')
              form.addEventListener('submit', event => {
                event.preventDefault()
                if (!this.canCheckout) return
                this.createPaymentMethod()
              })
            } catch (e) {
              console.log(e)
              this.formCreationError = e
            }
          } else {
            this.formCreationError = 'https://js.stripe.com/v3/ ---ERR: not loaded'
          }
        })
        .catch(() => {
          this.formCreationError = 'https://js.stripe.com/v3/ ---ERR: not loaded'
        })
    },
    createPaymentMethod() {
      if (!this.canCheckout) return

      const checkout_id = window.seg_checkout_id
      this.segmentTrackCheckout(checkout_id)

      this.$store.dispatch('globals_action', 'processing-payment')

      this.localLoading = true
      this.stripeError = null

      nextTick(() => {
        this.stripe
          .createPaymentMethod({
            type: 'card',
            card: this.cardNumber,
          })
          .then(result => {
            const payment_method_id = result?.paymentMethod?.id
            if (payment_method_id) {
              this.createSubscription({ payment_method_id })
            } else {
              this.handlePaymentFailure(result?.error)
              // throw new Error('payment_method_id is undefined')
            }
          })
          .catch(error => {
            this.handlePaymentFailure(error)
          })
      })
    },
    async createSubscription({ payment_method_id }) {
      const { plan } = this.selectedTierPlan
      gtm_sub_purchase_02({
        currencyCode: 'USD',
        payment_provider: 'stripe',
        game: this.customGame.toUpperCase(),
        type: plan.tier.toLowerCase(),
        paymentType: plan.interval.toLowerCase(),
        price: plan.price,
      })

      const data = {
        payment_method_id,
        plan_id: this.externalPlanID,
        checkout_id: window.seg_checkout_id,
      }
      // console.log(data)
      const res = await this.$store.dispatch('api_call', {
        method: 'POST',
        endpoint: 'payments/subscription/stripe/',
        data,
      })

      const data_status = res?.response?.data?.status
      const error_data_status = res?.error?.response?.data?.status
      const error_response_status = res?.error?.response?.status

      if (data_status === 'OK') {
        const { public_id } = res.response.data
        this.handlePaymentSuccess({ public_id })
      } else if (data_status === 'ACTION_REQUIRED') {
        const { client_secret, public_id } = res.response.data
        this.handle3DSecure({ client_secret, payment_method_id, public_id })
      } else if (error_data_status === 'CARD_DECLINED' || error_response_status === 400) {
        this.handlePaymentFailure(res, 'CARD_DECLINED')
      } else {
        this.handlePaymentFailure(res, error_response_status)
      }
    },
    handle3DSecure({ client_secret, payment_method_id, public_id }) {
      this.stripe
        .confirmCardPayment(client_secret, { payment_method: payment_method_id })
        .then(result => {
          if (result?.paymentIntent?.status === 'succeeded') {
            this.handlePaymentSuccess({ public_id })
          } else {
            // console.log(result)
            this.handlePaymentFailure(result?.error, result?.error?.response?.status)
          }
        })
        .catch(error => {
          this.handlePaymentFailure(error)
        })
    },
    async handlePaymentSuccess({ public_id }) {
      const confirm = await this.$store.dispatch('api_call', {
        method: 'POST',
        endpoint: '/payments/subscription/stripe/confirm/',
        data: { public_id },
      })
      if (confirm?.response?.status === 204 || confirm?.response?.status === 200) {
        localStorage.setItem('gtm_payment_session', Date.now())

        await this.$store.dispatch('api_call', {
          method: 'get',
          endpoint: 'account/settings/',
          hasCancel: false,
          mutation: 'api_account_settings',
          action: 'updateUserInfo',
        })
        this.$store.dispatch('gameformats_reinitialize_after_tier_update')

        this.segmentTrackSuccess()

        this.$store.dispatch('globals_action_reset')
        this.props.onSuccessPayment({
          payment_provider: 'stripe',
          payment_method: 'card',
        })
      } else {
        this.handlePaymentFailure('final-confirm-failed')
      }
    },
    handlePaymentFailure(error, status) {
      this.$store.dispatch('globals_action_reset')
      this.localLoading = false
      // console.log(error)
      this.stripeError = { error, status, message: error?.message }
      logSentry('Stripe Gateway: Payment failed', {
        error: JSON.stringify(error, null, 2),
        status,
        email: this.$store.getters.user?.email,
        selectedPlan: this.selectedTierPlan?.full_name,
        currentPlan: this.currentTierPlan?.full_name,
      })
    },
    segmentTrackCheckout(checkout_id) {
      seg_track('payment_info_entered', {
        checkout_id,
      })
    },
    segmentTrackSuccess() {
      const product = {
        plan: this.selectedTierPlan?.plan,
        price: { price: this.selectedTierPlan?.plan?.price },
      }
      seg_track('checkout_step_completed', {
        step: 2,
        checkout_type: 'Purchase Funnel',
        checkout_id: window.seg_checkout_id,
        payment_method: 'credit_card',
        // currency: 'USD',
        ...seg_getTotalAndCurrency([product]),
        products: [seg_getProduct(product)],
      })
    },
  },
  computed: {
    productionENV() {
      return import.meta.env.VITE_ENV === 'production' && import.meta.env.VITE_BRANCH === 'master'
    },
    externalPlanID() {
      const res = this.selectedPlan.external_plans.stripe.external_plan_id
      // const res = (this.selectedPayPlan || this.currentTierPlan).stripe.plan_id
      return res
    },
    customerCreated() {
      const status = this.$store.getters.api_get_status('api_payments_subscription_stripe_customer')
      return status === 204 || status === 200
    },
    customerCreatedLoading() {
      return this.$store.getters.api_get('api_payments_subscription_stripe_customer').loading
    },
    customerCreatedError() {
      const error = this.$store.getters.api_get_error('api_payments_subscription_stripe_customer')
      return error
    },
    canCheckout() {
      const formValid = Object.keys(this.completed).every(key => this.completed[key])
      const res = this.customerCreated && formValid
      return res
    },
  },
  watch: {
    externalPlanID() {
      this.stripeError = null
    },
  },
}
</script>

<style lang="scss" scoped>
.stripe-box {
  // padding: 2.5rem 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.input-cards {
  position: absolute;
  z-index: 4;
  top: 2.5rem;
  right: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
#payment-form {
  display: flex;
  flex-direction: column;
}

#card-number,
#card-expiry,
#card-cvc {
  padding: 1rem;
  margin-top: 0.25rem;
  background: var(--clr-white-0);
  border-radius: 8px;
  // height: 48px;
  // height: 3rem;
  // display: flex;
  // align-items: center;
  // justify-content: center;
  // position: relative;
}
.halved {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 1rem;
}

.stripe-error {
  padding-top: 4px;
  font-size: 1rem;
  color: var(--clr-error);
}
.error-input {
  box-shadow: 0px 0px 0px 1px var(--clr-error);
}
.safe-secure-checkout {
  display: flex;
  a {
    color: #90caf9 !important;
  }
  align-items: center;
  justify-content: center;
}
.svg-badges {
  background: white;
  border-radius: 99px;
  overflow: hidden;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  padding: 4px 0;
  &_svgs {
    min-width: 16rem;
    width: 16rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
.svg-badge {
  position: relative;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    position: absolute;
    z-index: 2;
  }
}

.svg-stripe {
  &:before {
    content: '';
    background: #003a70;
    width: 6rem;
    height: 1rem;
    position: absolute;
    z-index: 1;
  }
  left: -0.4rem;
  top: 2px;
  width: 8rem;
  height: 2rem;
  @extend .svg-badge;
}
.svg-encrypt {
  left: -0.5rem;

  width: 6.5rem;
  height: 2rem;
  @extend .svg-badge;
}
.refund-guarantee-message {
  color: #003a70;
  font-weight: 700;
  // font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 2rem;
  font-size: 0.8rem;
  &_icon {
    font-size: 1.25rem;
    margin-right: 0.25rem;
  }
}

@media (max-width: 767px) {
  .svg-badges {
    flex-direction: column;
    // width: 100%;
    border-radius: 99px;
    padding: 4px 1rem;
  }
  .input-cards {
    top: -3px;
    right: 0px;
    padding: 2px 6px;

    // background: #9e9e9e;
    border-radius: 8px;
  }
  .b-discover {
    background: #9e9e9e;
  }
  .icon-lock {
    position: absolute;
    top: 38px;
    right: 12px;
  }
  #card-number,
  #card-expiry,
  #card-cvc {
    height: 44px;
  }
  .refund-guarantee-message {
    padding: 0;
  }
}
</style>
